import type { WorkflowRun } from "@/lib/github/types";
import type { BucketSize, QueueTimeTrendRow } from "@/lib/analytics/types";
import { bucketByPeriod } from "@/lib/analytics/buckets";
import { p50, p95 } from "@/lib/analytics/percentiles";

/**
 * Returns the queue time (time between creation and actual start) in ms,
 * or null if run_started_at is missing/null (queued-never-started).
 */
export function computeQueueTimeMs(run: WorkflowRun): number | null {
  const started = run.run_started_at;
  if (started == null) return null;

  const createdAt = run.created_at;
  if (!createdAt) return null;

  const startMs = Date.parse(started);
  const createdMs = Date.parse(createdAt);
  if (Number.isNaN(startMs) || Number.isNaN(createdMs)) return null;

  return Math.max(0, startMs - createdMs);
}

function runDate(run: WorkflowRun): Date | null {
  const raw = run.run_started_at ?? run.created_at;
  if (!raw) return null;
  const ms = Date.parse(raw);
  if (Number.isNaN(ms)) return null;
  return new Date(ms);
}

/**
 * Computes p50/p95 queue time trend bucketed by the given BucketSize.
 * Runs with null queue time (run_started_at === null) are excluded from
 * the aggregates but not from totalRuns in the parent overview.
 */
export function computeQueueTimeTrend(
  runs: WorkflowRun[],
  bucket: BucketSize = "month",
): QueueTimeTrendRow[] {
  const byPeriod = bucketByPeriod(runs, bucket, (r) => runDate(r));
  const rows: QueueTimeTrendRow[] = [];

  for (const [period, group] of byPeriod) {
    const queueTimes: number[] = [];
    for (const run of group) {
      const qt = computeQueueTimeMs(run);
      if (qt !== null) queueTimes.push(qt);
    }
    rows.push({
      period,
      p50Ms: p50(queueTimes),
      p95Ms: p95(queueTimes),
      sampleSize: queueTimes.length,
    });
  }

  return rows;
}
